import React, { useState, useEffect } from 'react';
import SectionHeader from '../../components/rescue/SectionHeader';
import PriorityBadge from '../../components/rescue/PriorityBadge';
import LoadingState from '../../components/rescue/LoadingState';
import rescueService from '../../utils/rescueService';
import { formatDate } from '../../utils/rescueHelpers';
import { Layers, AlertOctagon, Check, MapPin, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AdminDuplicateRescuePage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await rescueService.getDuplicateReports();
      if (res.data.success) setReports(res.data.data);
    } catch (e) {
      console.error("Duplicate fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (reportId, action) => {
    try {
      const res = await rescueService.handleDuplicateAction(reportId, { action });
      if (res.data.success) {
        toast.success(`Report marked as ${action}`);
        fetchReports();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    }
  };

  if (loading) return <LoadingState message="Loading duplicate reports..." />;

  return (
    <div
      className="min-h-screen pt-[6rem] pb-[4rem] px-4 sm:px-6 lg:px-10
      shadow-[0_35px_90px_rgba(0,0,0,0.18),0_10px_30px_rgba(95,125,90,0.25)]
      relative overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[750px] h-[750px] bg-gradient-to-br from-[#7fa37a]/40 via-[#5f7d5a]/30 to-[#8b6b4c]/30 rounded-full blur-[150px] opacity-60 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <SectionHeader
          title="Duplicate Detection AI"
          description="Review suspected duplicate reports generated by proximity & time window matching."
          actions={
            <button onClick={fetchReports} className="p-2.5 rounded-xl bg-white/60 border border-[#8b6b4c]/30 text-[#6b7d67] hover:text-[#2f3e2c] transition">
              <RefreshCw className="w-4 h-4" />
            </button>
          }
        />

        {reports.length === 0 ? (
          <div className="mt-10 rounded-3xl p-12 text-center bg-gradient-to-br from-white/75 via-[#e5e3df]/75 to-[#a18463]/30 backdrop-blur-2xl border border-[#8b6b4c]/45 shadow-xl">
            <Layers className="w-12 h-12 text-[#7fa37a] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#2f3e2c] mb-2">No Duplicate Reports</h3>
            <p className="text-[#6b7d67] text-sm">The system has not flagged any duplicate rescue requests at this time.</p>
          </div>
        ) : (
          <div className="space-y-8 mt-6">
            {reports.map((report) => (
              <div
                key={report.id}
                className="rounded-3xl overflow-hidden
                bg-gradient-to-br from-white/75 via-[#e5e3df]/75 to-[#a18463]/30
                backdrop-blur-2xl border border-[#8b6b4c]/45 shadow-[0_25px_80px_rgba(0,0,0,0.12)]"
              >
                {/* Header */}
                <div className="p-4 sm:p-5 border-b border-[#8b6b4c]/20 bg-white/45 backdrop-blur-xl flex justify-between items-center gap-4 flex-wrap">
                  <div className="flex items-center">
                    <Layers className="w-5 h-5 text-[#5f7d5a] mr-3" />
                    <h3 className="font-bold text-[#2f3e2c]">Suspected Duplicate Set</h3>
                  </div>
                  <div className="flex items-center bg-white/70 px-3 py-1.5 rounded-xl border border-[#8b6b4c]/30 shadow-sm">
                    <span className="text-xs font-bold text-[#6b7d67] mr-2 uppercase tracking-wide">AI Match Score</span>
                    <span className="font-black text-[#2f3e2c]">{(report.confidenceScore * 100).toFixed(0)}%</span>
                  </div>
                </div>

                {/* Comparison Grid */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
                    <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-[#8b6b4c]/20 -ml-px" />
                    <div className="hidden md:flex absolute top-1/2 left-1/2 -ml-4 -mt-4 w-8 h-8 rounded-full bg-white/80 border border-[#8b6b4c]/25 items-center justify-center text-xs font-bold text-[#6b7d67] shadow-sm">
                      VS
                    </div>

                    {/* Main Request */}
                    <div>
                      <div className="flex justify-between items-center mb-4 gap-3">
                        <h4 className="font-bold text-[#2f3e2c]">
                          Original <span className="text-xs font-medium text-[#6b7d67] border border-[#8b6b4c]/25 px-2 py-0.5 rounded-full ml-2 bg-white/55">{report.mainRequestId?.split('-')[0]}</span>
                        </h4>
                        <PriorityBadge priority={report.mainRequest?.priority} />
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white/55 p-3 rounded-2xl text-sm border border-[#8b6b4c]/25 text-[#4e5f4a]">
                          <span className="font-medium text-[#2f3e2c] block mb-1">Time:</span>
                          {formatDate(report.mainRequest?.createdAt)}
                        </div>
                        <div className="bg-white/55 p-3 rounded-2xl text-sm border border-[#8b6b4c]/25 text-[#4e5f4a]">
                          <span className="font-medium text-[#2f3e2c] flex items-center mb-1"><MapPin className="w-3 h-3 mr-1" /> Location:</span>
                          {report.mainRequest?.incidentAddress || 'Unknown'}
                        </div>
                        <div className="bg-white/55 p-3 rounded-2xl text-sm border border-[#8b6b4c]/25 text-[#4e5f4a]">
                          <span className="font-medium text-[#2f3e2c] block mb-1">Desc:</span>
                          "{report.mainRequest?.description}"
                        </div>
                      </div>
                    </div>

                    {/* Duplicate Request */}
                    <div>
                      <div className="flex justify-between items-center mb-4 gap-3">
                        <h4 className="font-bold text-[#2f3e2c]">
                          Duplicate <span className="text-xs font-bold text-[#8b6b4c] bg-white/65 border border-[#8b6b4c]/30 px-2 py-0.5 rounded-full ml-2">{report.duplicateRequestId?.split('-')[0]}</span>
                        </h4>
                        <PriorityBadge priority={report.duplicateRequest?.priority} />
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white/55 p-3 rounded-2xl text-sm border border-[#8b6b4c]/25 text-[#4e5f4a]">
                          <span className="font-medium text-[#2f3e2c] block mb-1">Time:</span>
                          {formatDate(report.duplicateRequest?.createdAt)}
                        </div>
                        <div className="bg-white/55 p-3 rounded-2xl text-sm border border-[#8b6b4c]/25 text-[#4e5f4a]">
                          <span className="font-medium text-[#2f3e2c] flex items-center mb-1"><MapPin className="w-3 h-3 mr-1" /> Location:</span>
                          {report.duplicateRequest?.incidentAddress || 'Unknown'} <span className="text-xs text-[#8b6b4c] font-bold ml-2">(Same area)</span>
                        </div>
                        <div className="bg-white/55 p-3 rounded-2xl text-sm border border-[#8b6b4c]/25 text-[#4e5f4a]">
                          <span className="font-medium text-[#2f3e2c] block mb-1">Desc:</span>
                          "{report.duplicateRequest?.description}"
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-white/40 backdrop-blur-xl p-4 border-t border-[#8b6b4c]/20 flex flex-wrap justify-end gap-3">
                  <button
                    onClick={() => handleAction(report.id, 'REJECTED')}
                    className="flex items-center px-4 py-2.5 rounded-xl bg-white/55 border border-[#8b6b4c]/40 text-[#2f3e2c] font-semibold hover:bg-white/70 hover:shadow-md transition"
                  >
                    Mark as Unique
                  </button>
                  <button
                    onClick={() => handleAction(report.id, 'REJECTED')}
                    className="flex items-center px-4 py-2.5 rounded-xl bg-white/55 border border-[#8b6b4c]/40 text-[#8b6b4c] font-semibold hover:bg-white/70 hover:shadow-md transition"
                  >
                    <AlertOctagon className="w-4 h-4 mr-2" /> Ignore New Request
                  </button>
                  <button
                    onClick={() => handleAction(report.id, 'CONFIRMED')}
                    className="flex items-center px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#5f7d5a]/55 via-[#7fa37a] to-[#8b6b4c] border border-[#d6e2d3] text-black/75 font-semibold hover:scale-[1.02] hover:shadow-lg transition duration-300"
                  >
                    <Check className="w-4 h-4 mr-2" /> Confirm Duplicate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDuplicateRescuePage;